/**
 * CR318: Health classification logic
 */

import type {
  SnapshotCheck,
  EdgeCheck,
  AuthCheck,
  DbCheck,
  HealthClassification,
} from "./types";

export function classifyHealth(
  snapshot: SnapshotCheck | null,
  edge: EdgeCheck | null,
  auth: AuthCheck | null,
  db: DbCheck | null
): HealthClassification {
  const reasons: string[] = [];

  // Check 1: Snapshot (highest priority)
  if (snapshot?.status === "MISSING") {
    return {
      status: "outage",
      label: "No Snapshot",
      reason: "No snapshot is currently published. The public site shows 0 deals.",
      secondaryReason: edge?.status === "ERROR"
        ? `Also: Edge function returning ${edge.httpStatus || "error"}`
        : null,
      confidence: "high",
    };
  }

  // Check 2: Edge reachability
  if (edge?.status === "ERROR" || edge?.status === "TIMEOUT") {
    const httpCodes = [502, 503, 504];
    const isServerError = edge.httpStatus && httpCodes.includes(edge.httpStatus);
    return {
      status: "outage",
      label: "Edge Down",
      reason: edge.fetchError?.message
        ? `Edge function unreachable: ${edge.fetchError.message}`
        : `Edge function returning HTTP ${edge.httpStatus}`,
      secondaryReason: snapshot?.status === "STALE" ? "Also: Snapshot may be stale" : null,
      confidence: isServerError ? "high" : "medium",
    };
  }

  // Check 3: Auth issues
  if (auth?.status === "NO_SESSION" || auth?.status === "EXPIRED") {
    return {
      status: "degraded",
      label: "Auth",
      reason: auth.status === "NO_SESSION"
        ? "No active session. Please log in to access admin features."
        : "Session expired. Please refresh the page to re-authenticate.",
      secondaryReason: null,
      confidence: "high",
    };
  }

  if (auth?.status === "FORBIDDEN") {
    return {
      status: "degraded",
      label: "Auth",
      reason: `Access denied (HTTP ${auth.errorCode || 403}). You may not have admin permissions.`,
      secondaryReason: null,
      confidence: "high",
    };
  }

  // Check 4: DB sanity
  if (db?.status === "ERROR") {
    return {
      status: "degraded",
      label: "DB",
      reason: db.message || "Database connectivity issue detected.",
      secondaryReason: null,
      confidence: "medium",
    };
  }

  // Check 5: Stale snapshot (warning, not outage)
  if (snapshot?.status === "STALE") {
    reasons.push("Snapshot may be stale");
  }

  // All checks passed
  if (snapshot?.status === "OK" && edge?.status === "OK" && auth?.status === "OK" && db?.status === "OK") {
    return {
      status: "healthy",
      label: null,
      reason: "All production systems operational.",
      secondaryReason: null,
      confidence: "high",
    };
  }

  return {
    status: "healthy",
    label: null,
    reason: reasons.length > 0 ? reasons.join(". ") : "Health status pending verification.",
    secondaryReason: null,
    confidence: "low",
  };
}
